import { useState, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import { APP } from './tokens';
import { APP_ERROR_EVENT, type AppErrorDetail } from '../../lib/appErrorBanner';

/* ─── Paper design 2XH-0 — dark toast at bottom-center ─────────────── */

const CONTAINER: CSSProperties = {
  position: 'fixed',
  bottom: 32,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 9999,
  minWidth: 340,
  maxWidth: 480,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 18px',
  borderRadius: 10,
  backgroundColor: 'var(--rate-limit-toast-bg)',
  boxShadow: 'var(--rate-limit-toast-shadow)',
  fontFamily: APP.fontFamily,
};

const ICON_WRAP: CSSProperties = {
  flexShrink: 0,
  width: 16,
  height: 16,
};

const TEXT_WRAP: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const TITLE: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '16px',
  color: 'var(--rate-limit-toast-fg)',
  margin: 0,
};

const SUBTITLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 400,
  lineHeight: '14px',
  color: 'var(--rate-limit-toast-muted)',
  marginTop: 2,
};

const CLOSE_BTN: CSSProperties = {
  flexShrink: 0,
  width: 14,
  height: 14,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/* Warning icon — circle with exclamation mark */
function WarningIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6.5" stroke="#D97645" strokeWidth="1.5" />
      <line x1="8" y1="5" x2="8" y2="9" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11" r="0.75" fill="#D97645" />
    </svg>
  );
}

/* Close X icon */
function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4L10 10M10 4L4 10" stroke="var(--rate-limit-toast-muted)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function AppErrorBanner() {
  const [banner, setBanner] = useState<AppErrorDetail | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    setBanner(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent<AppErrorDetail>).detail;
      if (timerRef.current) clearTimeout(timerRef.current);
      setBanner(d);
      const ms = d.autoDismissMs ?? 6000;
      if (ms > 0) {
        timerRef.current = setTimeout(() => {
          setBanner(null);
          timerRef.current = null;
        }, ms);
      }
    };
    window.addEventListener(APP_ERROR_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(APP_ERROR_EVENT, handler as EventListener);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!banner) return null;

  return (
    <div style={CONTAINER}>
      <div style={ICON_WRAP}>
        <WarningIcon />
      </div>
      <div style={TEXT_WRAP}>
        <div style={TITLE}>{banner.title}</div>
        {banner.description && <div style={SUBTITLE}>{banner.description}</div>}
      </div>
      <button style={CLOSE_BTN} onClick={dismiss} aria-label="Dismiss">
        <CloseIcon />
      </button>
    </div>
  );
}
